/**
 * Next year's objectives, inside this year's year-end check-in (HR UAT list #27).
 *
 * HR sets next year's objectives in the same meeting as the year-end check-in. Before it,
 * the Team Member or People Leader brings objectives in (Add New, from this year, from the
 * Objective Library) as pre-work, both from here as well (Victor, 30 Oct 2026: the Team Member
 * too, from their side of the check-in); in the check-in the People Leader goes through them
 * here and agrees them. The meeting that opens their approval is this check-in
 * (performance-service app/services/year_end_objective_setting.py), so no second meeting is
 * booked.
 */
import { useCallback, useEffect, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { CalendarPlus, Goal, Loader2 } from 'lucide-react'
import { useAuth } from '@/providers/AuthProvider'
import { performanceService } from '@/services/api/performanceService'
import ObjectiveConfigurationDialog from '@/components/performance/manager/ObjectiveConfigurationDialog'
import { useObjectiveConfigHandlers } from '@/hooks/useObjectiveConfigHandlers'
import { useOwnObjectiveHandlers } from '@/hooks/useOwnObjectiveHandlers'

interface NextYearObjectivesStepProps {
  memberId: string
  memberName: string
  memberRole?: string
  /** The year this check-in reviews; the objectives are for the year after. */
  reviewYear: number
  userRole: 'manager' | 'employee'
}

const STATUS: Record<string, { label: string; className: string }> = {
  pending_approval: { label: 'Waiting for approval', className: 'bg-amber-100 text-amber-800' },
  active: { label: 'Agreed', className: 'bg-emerald-100 text-emerald-800' },
  rejected: { label: 'Not agreed', className: 'bg-red-100 text-red-800' },
}

export function NextYearObjectivesStep({ memberId, memberName, memberRole, reviewYear, userRole }: NextYearObjectivesStepProps) {
  const { user } = useAuth()
  const year = reviewYear + 1
  const [objectives, setObjectives] = useState<any[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)

  const load = useCallback(async () => {
    try {
      const data = await performanceService.getEmployeeObjectives(memberId, { year })
      setObjectives((data.objectives || []).filter((o: any) => o.status !== 'withdrawn'))
      setFailed(false)
    } catch (error) {
      console.error(`Couldn't load the ${year} objectives:`, error)
      setFailed(true)
    }
  }, [memberId, year])

  useEffect(() => { load() }, [load])

  const isLeader = userRole === 'manager'
  const handlers = useObjectiveConfigHandlers({
    managerId: isLeader ? user?.employeeId || '' : '',
    employeeId: memberId,
    employeeName: memberName,
    employeeRole: memberRole,
    onRefreshData: load,
    year,
  })
  // The Team Member adds theirs as on My Objectives: as their own, for next year
  const own = useOwnObjectiveHandlers({ user, year, onChange: load })

  const pending = (objectives ?? []).filter(o => o.status === 'pending_approval')
  const agreed = (objectives ?? []).filter(o => o.status === 'active')
  const hasSome = !!objectives && objectives.length > 0

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CalendarPlus className="h-5 w-5 text-heritage-blue" />
            {year} objectives
          </CardTitle>
          <CardDescription>
            {isLeader
              ? `Agree ${memberName.split(' ')[0]}'s objectives for ${year} in this check-in. Add new ones, bring in ${reviewYear} objectives or pick from the Objective Library, then approve them.`
              : `Your objectives for ${year} are agreed in this check-in. Before it, add yours or bring in ${reviewYear} objectives here (or on My Objectives, choosing ${year}); your People Leader approves them in the check-in.`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {failed ? (
            <p className="text-sm text-red-700">The {year} objectives couldn't be loaded. Please try again.</p>
          ) : objectives === null ? (
            <div className="flex items-center gap-2 text-muted-foreground py-6 justify-center">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading...
            </div>
          ) : objectives.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Goal className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>No objectives for {year} yet.</p>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-700">
                {agreed.length} agreed, {pending.length} waiting for approval.
              </p>
              <ul className="divide-y rounded-lg border" aria-label={`${year} objectives`}>
                {objectives.map(o => {
                  const status = STATUS[o.status] ?? { label: o.status, className: 'bg-gray-100 text-gray-800' }
                  return (
                    <li key={o.id} className="flex items-start justify-between gap-3 p-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{o.title}</p>
                        {o.kpa?.display_name && <p className="text-xs text-muted-foreground mt-0.5">{o.kpa.display_name}</p>}
                      </div>
                      <Badge variant="secondary" className={`shrink-0 ${status.className}`}>{status.label}</Badge>
                    </li>
                  )
                })}
              </ul>
            </>
          )}

          <Button onClick={() => setDialogOpen(true)} className="bg-heritage-blue hover:bg-heritage-blue/90">
            <CalendarPlus className="h-4 w-4 mr-2" />
            {isLeader
              ? (hasSome ? `Review and agree the ${year} objectives` : `Set the ${year} objectives`)
              : (hasSome ? `Add or change my ${year} objectives` : `Add my ${year} objectives`)}
          </Button>
        </CardContent>
      </Card>

      {dialogOpen && (
        <ObjectiveConfigurationDialog
          open={dialogOpen}
          onOpenChange={(open) => { setDialogOpen(open); if (!open) load() }}
          initialTab={pending.length > 0 ? 'pending' : 'allocate'}
          selectedYear={year}
          userRole={userRole}
          teamMember={{
            employee_id: memberId,
            // The Team Member's own side reads "Me" in the check-in: their name, as on My Objectives
            display_name: isLeader ? memberName : user?.displayName || memberName,
            job_title: memberRole || '',
            department: '',
            campus: '',
            objectives: {
              total: objectives?.length ?? 0, onTrack: 0, atRisk: 0, offTrack: 0, completed: 0,
              pending_approval: pending.length, progress: 0,
            },
            pending_objectives: pending,
            active_objectives: agreed,
            rejected_objectives: (objectives ?? []).filter(o => o.status === 'rejected'),
          } as any}
          onObjectivesChange={load}
          {...(isLeader ? {
            onApprove: async (id: number, feedback?: string, edits?: any, employeeId?: string) => {
              await handlers.handleApproveObjective(id, feedback, edits, employeeId)
              await load()
            },
            onReject: async (id: number, reason: string) => {
              await handlers.handleRejectObjective(id, reason)
              await load()
            },
            onUpdate: handlers.handleUpdateObjective,
            onAllocate: handlers.handleAllocateObjectives,
            onObjectivesAdded: async () => {
              await handlers.ensureCollaborativeSession('objective_setting')
              await handlers.loadCollaborativeSessions()
              await load()
            },
            onSessionChange: handlers.loadCollaborativeSessions,
          } : {
            onApprove: own.handleApprove,
            onReject: own.handleReject,
            onUpdate: own.handleUpdate,
            onAllocate: own.handleAllocate,
            onObjectivesAdded: own.handleObjectivesAdded,
          })}
        />
      )}
    </div>
  )
}
